import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import { FC } from 'react';

import { fetchAuctions } from '@/shared/api/buyer';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { MockAuction } from '@/shared/stubs/auction';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { nullableData } from '@/shared/utils/http';
import { TRACER_NAME_BUILDER, getSpan } from '@/shared/utils/tracer';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import sharedStyle from '../shared.module.scss';
import { mapRemoteAuctionToCard } from './auctionCard';
import { Auction, AuctionInput, Row } from './Row';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
  auctionIds: AuctionInput[];
}

type Props = BuilderInput & {
  locale?: string;
  auctions?: Auction[];
};

export const Auctions: FC<Props> = ({
  componentLinkId,
  title,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  auctionIds = [],
  auctions = [],
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  return (
    <div
      {...attributes}
      id={componentLinkId}
      data-testid="auctions"
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={action}
        actionVariant={actionVariant}
        locale={blockLocale}
        headerClassName={cn('u-wrapper', {
          'u-m-b-xl u-m-b-lg-xxl-6': auctionIds && auctionIds.length > 0,
        })}
        mobileActionClassName={'u-wrapper'}
      >
        <Row locale={blockLocale} auctionIds={auctionIds} auctions={auctions} />
      </ComponentHeader>
    </div>
  );
};

async function fetchAuctionItems(
  locale: string,
  auctionIds: AuctionInput[]
): Promise<Auction[]> {
  if (auctionIds.length === 0) return [];

  const ids = auctionIds.map((item) => item.auctionId);
  const eitherAuctions = await fetchAuctions(locale, ids);
  const remoteAuctions = pipe(eitherAuctions, nullableData) || [];
  const auctionsMap = remoteAuctions.reduce(
    (out, auction) => ({
      ...out,
      [auction.id]: mapRemoteAuctionToCard(auction),
    }),
    {} as {
      [key in number]: Auction;
    }
  );
  return ids.map((id) => auctionsMap[id] || MockAuction(locale, id));
}

export function dataFetcher(locale: string) {
  return async ({ auctionIds = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'Auctions.dataFetcher');
    const auctions = await fetchAuctionItems(locale, auctionIds);
    span.end();
    return {
      locale,
      auctions,
    };
  };
}

export const NAME = 'Auctions';

export function componentRegister() {
  Builder.registerComponent(Auctions, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">Title</span>',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'auctionIds',
        type: 'list',
        friendlyName: 'Auctions',
        subFields: [
          {
            name: 'auctionId',
            friendlyName: 'Auction ID',
            type: 'number',
            required: true,
          },
        ],
      },
    ],
    noWrap: true,
  });

  Builder.registerComponent(Auctions, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif tw:text-brand-primary">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
        useCatawikiPlainEditor: false,
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'auctionIds',
        type: 'list',
        friendlyName: 'Auctions',
        subFields: [
          {
            name: 'auctionId',
            friendlyName: 'Auction ID',
            type: 'number',
            required: true,
          },
        ],
      },
    ],
    noWrap: true,
  });

  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
